(function () {
  'use strict';
  if (window.FiAklaCartIntegrity) return;
  let queue = Promise.resolve();
  let cleanupPending = true;
  let cleanupTimer = null;
  const productTagCache = new Map();
  const weeklyMealTypes = new Set([
    'Main', 'Side', 'Dessert', 'Breakfast', 'Smoothie',
    'طبق رئيسي', 'طبق جانبي', 'حلو', 'فطار', 'سموذي'
  ]);
  const route = path => (window.Shopify?.routes?.root || '/') + path;
  async function request(path, data) {
    const response = await fetch(route(path), {
      method: data ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(data ? { body: JSON.stringify(data) } : {})
    });
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(result.description || result.message || 'Unable to update cart');
    return result;
  }
  function run(task) {
    const result = queue.then(task);
    queue = result.catch(() => {});
    return result;
  }
  function setCleanupPending(pending) {
    cleanupPending = pending;
    document.dispatchEvent(new CustomEvent('fi-akla:cart-cleanup-state', { detail: { pending } }));
  }
  function isWeeklyMeal(item) {
    return Boolean(item && weeklyMealTypes.has(String(item.product_type || '')) && !item.properties?.['_Parent variant']);
  }
  function normalizeTags(tags) {
    if (Array.isArray(tags)) return tags.map(tag => String(tag).trim().toLowerCase());
    return String(tags || '').split(',').map(tag => tag.trim().toLowerCase()).filter(Boolean);
  }
  async function productTags(item, fresh) {
    const handle = String(item?.handle || '');
    if (!handle) throw new Error('A weekly meal is missing its product handle.');
    const cached = productTagCache.get(handle);
    if (!fresh && cached && Date.now() - cached.savedAt < 30000) return cached.tags;
    const response = await fetch(route('products/' + encodeURIComponent(handle) + '.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to verify whether ' + item.title + ' is on the current menu.');
    const product = await response.json();
    const tags = normalizeTags(product.tags);
    productTagCache.set(handle, { tags, savedAt: Date.now() });
    return tags;
  }
  async function expiredWeeklyMeals(cart, strict) {
    const meals = (cart.items || []).filter(isWeeklyMeal);
    if (!meals.length) return [];
    const schedule = window.FiAklaDeliverySchedule;
    if (!schedule || typeof schedule.getAvailableDeliveries !== 'function') {
      if (strict) throw new Error('Delivery availability could not be verified.');
    } else if (!schedule.getAvailableDeliveries(Date.now(), 2).length) {
      return meals;
    }
    const checks = await Promise.all(meals.map(async item => {
      try {
        const tags = await productTags(item, strict);
        return tags.includes('past-menu') || !tags.includes('current-week');
      } catch (error) {
        if (strict) throw error;
        console.error('[Fi Akla] Could not verify menu status:', error);
        return false;
      }
    }));
    return meals.filter((item, index) => checks[index]);
  }
  async function removeExpiredMenuItems(cart, strict) {
    const expired = await expiredWeeklyMeals(cart, strict);
    if (!expired.length) return { cart, removed: [] };
    const expiredKeys = new Set(expired.map(item => item.key));
    const expiredVariants = new Set(expired.map(item => String(item.variant_id)));
    const linkedKeys = new Set((cart.items || [])
      .filter(item => expiredVariants.has(String(item.properties?.['_Parent variant'] || '')))
      .map(item => item.key));
    const keysToRemove = [...new Set([...expiredKeys, ...linkedKeys])];
    for (const key of keysToRemove) {
      cart = await request('cart/change.js', { id: key, quantity: 0 });
    }
    for (const item of (cart.items || []).filter(isWeeklyMeal)) {
      if (!item.properties?.['Delivery slot']) continue;
      const properties = Object.assign({}, item.properties);
      delete properties['Delivery slot'];
      cart = await request('cart/change.js', { id: item.key, quantity: item.quantity, properties });
    }
    const attributes = Object.assign({}, cart.attributes || {}, {
      'Delivery slot': '',
      '_delivery_date': '',
      '_delivery_window': ''
    });
    const note = (cart.note || '').replace(/(?:\n|^)Fi Akla delivery: [^\n]*/g, '').trim();
    cart = await request('cart/update.js', { updates: {}, attributes, note });
    try { window.sessionStorage.setItem('fiAklaExpiredMenuRemoved', '1'); } catch (error) {}
    return { cart, removed: expired };
  }
  function showExpiredMenuNotice() {
    let shouldShow = false;
    try {
      shouldShow = window.sessionStorage.getItem('fiAklaExpiredMenuRemoved') === '1';
      if (shouldShow) window.sessionStorage.removeItem('fiAklaExpiredMenuRemoved');
    } catch (error) {}
    if (!shouldShow) return;
    document.querySelectorAll('[data-fi-menu-expired]').forEach(element => { element.hidden = false; });
  }
  async function refreshAfterCleanup() {
    showExpiredMenuNotice();
    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }
    if (typeof window.fiAklaRefreshCartDrawer === 'function') await window.fiAklaRefreshCartDrawer();
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    window.setTimeout(showExpiredMenuNotice, 100);
  }
  function scheduleCleanup() {
    clearTimeout(cleanupTimer);
    setCleanupPending(true);
    cleanupTimer = window.setTimeout(function () {
      run(async function () {
        const result = await removeExpiredMenuItems(await request('cart.js'), false);
        setCleanupPending(false);
        if (result.removed.length) await refreshAfterCleanup();
        else showExpiredMenuNotice();
      }).catch(function (error) {
        console.error('[Fi Akla] Expired menu cleanup failed:', error);
        setCleanupPending(false);
      });
    }, 20);
  }
  async function save(attributes, extra) {
    const cart = await request('cart.js');
    const expected = Object.assign({}, cart.attributes || {}, attributes);
    const updated = await request('cart/update.js', Object.assign({}, extra, { attributes: expected }));
    for (const key of Object.keys(expected)) {
      if (String(updated.attributes?.[key] || '') !== String(expected[key] || '')) throw new Error('Cart details were not saved');
    }
    return updated;
  }
  function parents(cart, variant) {
    return cart.items.filter(item => String(item.variant_id) === String(variant) && item.properties?.['_Extra protein add-on SKU']);
  }
  async function reconcile(cart) {
    const variants = [...new Set(cart.items.filter(item => item.properties?.['_Parent variant']).map(item => String(item.properties['_Parent variant'])))];
    for (const variant of variants) {
      const expected = parents(cart, variant).reduce((sum, item) => sum + item.quantity, 0);
      const addons = cart.items.filter(item => String(item.properties?.['_Parent variant'] || '') === variant);
      for (const duplicate of addons.slice(1)) cart = await request('cart/change.js', { id: duplicate.key, quantity: 0 });
      const current = cart.items.find(item => item.key === addons[0].key);
      if (current && current.quantity !== expected) cart = await request('cart/change.js', { id: current.key, quantity: expected });
    }
    for (const parent of cart.items.filter(item => item.properties?.['_Extra protein add-on SKU'])) {
      const addons = cart.items.filter(item => String(item.properties?.['_Parent variant'] || '') === String(parent.variant_id));
      const expected = parents(cart, parent.variant_id).reduce((sum, item) => sum + item.quantity, 0);
      if (addons.length !== 1 || addons[0].quantity !== expected) throw new Error('Please remove and re-add this meal with extra protein.');
    }
    return cart;
  }
  window.FiAklaCartIntegrity = {
    isCleanupPending: () => cleanupPending,
    cleanupExpiredMenu: () => run(async () => removeExpiredMenuItems(await request('cart.js'), true)),
    updateAttributes: attributes => run(() => save(attributes)),
    changeQuantity: config => run(async () => {
      let cart = await request('cart.js');
      let item = cart.items.find(entry => entry.key === config.key);
      if (!item) throw new Error('Your cart changed. Please refresh and try again.');
      if (item.properties?.['_Parent variant']) {
        const linked = parents(cart, item.properties['_Parent variant']);
        if (Number(config.quantity) === 0) {
          for (const parent of linked) {
            const properties = Object.assign({}, parent.properties);
            properties['_Extra protein add-on SKU'] = '';
            properties['Extra protein'] = '';
            cart = await request('cart/change.js', { id: parent.key, quantity: parent.quantity, properties });
          }
        } else {
        if (linked.length !== 1) throw new Error('Please adjust extra protein using the meal quantity.');
        item = linked[0];
        }
      }
      cart = await request('cart/change.js', { id: item.key, quantity: config.quantity });
      cart = await reconcile(cart);
      return request('cart/update.js', { updates: {}, sections: config.sections, sections_url: config.sections_url });
    }),
    prepareCheckout: (attributes, slot) => run(async () => {
      setCleanupPending(true);
      try {
        const cleanup = await removeExpiredMenuItems(await request('cart.js'), true);
        if (cleanup.removed.length) {
          setCleanupPending(false);
          await refreshAfterCleanup();
          throw new Error('Previous-menu meals were removed. Review the cart before checkout.');
        }
        let cart = await reconcile(cleanup.cart);
        if (slot) {
          for (const item of cart.items) {
            const properties = item.properties || {};
            const noMealDelivery = item.product_type === 'Meal Pass' ||
              item.handle === 'fi-akla-meal-pass' ||
              /^PASS-(LIGHT|REGULAR|FULL)-(4W|8W|12W)$/.test(item.sku || '') ||
              item.product_type === 'Service' ||
              properties['_Booking type'] === 'Invite Us Over' ||
              properties['_Booking type'] === 'Invite Us Over add-on' ||
              /^invite-us-over-/.test(item.handle || '');
            if (noMealDelivery) {
              if (properties['Delivery slot']) {
                const cleanedProperties = Object.assign({}, properties);
                cleanedProperties['Delivery slot'] = '';
                cart = await request('cart/change.js', { id: item.key, quantity: item.quantity, properties: cleanedProperties });
              }
              continue;
            }
            if (item.properties?.['Delivery slot'] !== slot) {
              cart = await request('cart/change.js', { id: item.key, quantity: item.quantity, properties: Object.assign({}, item.properties, { 'Delivery slot': slot }) });
            }
          }
        }
        const note = (cart.note || '').replace(/(?:\n|^)Fi Akla delivery: [^\n]*/g, '').trim();
        const saved = await save(attributes, slot ? { note: [note, 'Fi Akla delivery: ' + slot].filter(Boolean).join('\n') } : {});
        setCleanupPending(false);
        return saved;
      } catch (error) {
        setCleanupPending(false);
        throw error;
      }
    })
  };
  document.addEventListener('cart:open', scheduleCleanup);
  document.addEventListener('cart:updated', scheduleCleanup);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { showExpiredMenuNotice(); scheduleCleanup(); }, { once: true });
  } else {
    showExpiredMenuNotice();
    scheduleCleanup();
  }
})();
